import { useState } from "react";
import { Box, Button, Paper, Typography, useMediaQuery } from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  NumberField,
  SelectField,
  SimpleList,
  TextField,
  Title,
  useGetList,
  useNotify,
  useStore,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { EmptyList } from "server/components";
import SecuredComponent from "server/security/SecuredComponent";
import CustomFilter from "features/CustomFilter";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import permissions from "app/permissions";
import { doctoralRequests } from "api/doctoralApi";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { DoctoralRankingResultStatusChoices } from "utils/choices";
import { doctoralSeatLabel } from "../seatLabel";

const campaignsResource = doctoralRequests.getDoctoralCampaignsSelect();
const approvedCandidaciesResource = doctoralRequests.getApprovedCandidacies();
const APPROVED_CANDIDACIES_STORE_KEY = "approvedDoctoralCandidacies.filters";

const ACCEPTED_STATUS = 2;
const ENROLLED_STATUS = 4;

const EnrollButton = ({ record, seatId }) => {
  const navigate = useNavigate();

  if (record.status !== ACCEPTED_STATUS) return null;

  return (
    <SecuredComponent permission={permissions.canEnrollDoctorants}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() =>
          navigate("/doctoral/enroll-doctorant", {
            state: {
              doctorantCandidacyId: record.doctorantCandidacyId,
              doctoralSeatId: seatId,
            },
          })
        }
      >
        Записване
      </Button>
    </SecuredComponent>
  );
};

const ListApprovedCandidacies = () => {
  const notify = useNotify();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // filters drives the <List> and the gate (persisted via useStore);
  // campaignId/seatId are the draft selections bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(APPROVED_CANDIDACIES_STORE_KEY, null);
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);
  const [seatId, setSeatId] = useState(filters?.seatId ?? null);

  const { data: campaignsData = [] } = useGetList(campaignsResource, {
    pagination: { page: 1, perPage: 100 },
  });

  const { data: seatsData = [] } = useGetList(
    doctoralRequests.getDoctoralSeatsByCampaignSelect(campaignId ?? 0),
    { pagination: { page: 1, perPage: 100 } },
    { enabled: !!campaignId },
  );

  const seatChoices = seatsData.map(s => ({
    id: s.id,
    name: doctoralSeatLabel({ doctoralProgramNameBg: s.doctoralProgramNameBg, studyTypeId: s.studyTypeId }),
  }));

  const onSubmit = () => {
    if (!campaignId) {
      notify("Моля изберете кампания!", { type: "error" });
      return;
    }
    if (!seatId) {
      notify("Моля изберете програма / форма на обучение!", { type: "error" });
      return;
    }
    setFilters({ campaignId, seatId });
  };

  return (
    <>
      {!filters && <Title title="Приети докторанти" />}
      <CustomFilter
        title="Приети кандидатури"
        cardWidth="900px"
        buttonLabel="Провери"
        buttonOnClick={onSubmit}
        filters={[
          {
            label: "Кампания",
            value: campaignId,
            setValue: v => {
              setCampaignId(v);
              setSeatId(null);
            },
            type: "autocomplete",
            multiple: false,
            choices: campaignsData,
          },
          {
            label: "Програма / форма на обучение",
            value: seatId,
            setValue: setSeatId,
            type: "autocomplete",
            multiple: false,
            disable: !campaignId,
            choices: seatChoices,
          },
        ]}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography variant="inherit" align="center" color="secondary">
              Изберете кампания и програма / форма на обучение за преглед на приетите кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          resource={approvedCandidaciesResource}
          filter={{ campaignId: filters.campaignId, doctoralSeatId: filters.seatId }}
          actions={false}
          disableSyncWithLocation
          title="Приети докторанти"
          sx={settings.style.blockStyle}
          empty={<EmptyList />}
        >
          {isMobile ? (
            <SimpleList
              empty={<EmptyList />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Вх. номер", value: record?.registrationNumber }]} />
                  <MobileListRow fields={[{ label: "Име", value: record?.fullName }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Бал",
                        value: (
                          <NumberField
                            source="score"
                            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                            sortable={false}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Желание", value: record?.seatPriorityNumber }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус",
                        value: (
                          <SelectField
                            source="status"
                            choices={DoctoralRankingResultStatusChoices}
                            sortable={false}
                          />
                        ),
                      },
                    ]}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <EnrollButton record={record} seatId={filters.seatId} />
                  </div>
                </div>
              )}
            />
          ) : (
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <TextField source="registrationNumber" label="Вх. номер" sortable={false} align="center" />
              <TextField source="fullName" label="Име" sortable={false} />
              <NumberField
                source="score"
                label="Бал"
                options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                sortable={false}
                align="center"
              />
              <TextField source="seatPriorityNumber" label="Желание" sortable={false} align="center" />
              <SelectField
                source="status"
                label="Статус"
                choices={DoctoralRankingResultStatusChoices}
                sortable={false}
                align="center"
              />
              <FunctionField
                label="Действия"
                align="center"
                render={record => <EnrollButton record={record} seatId={filters.seatId} />}
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListApprovedCandidacies;
